(function() {
  // Klaviyo settings are intentionally surfaced here for easy updates.
  const KLAVIYO_API_KEY = 'SFEQpt';
  const KLAVIYO_LIST_ID = 'RmbwfZ';

  // Wishlist storage must always use this key for the saved product ID array.
  const STORAGE_KEY = 'aluma_wishlist';
  const STORAGE_ITEMS_KEY = 'aluma_wishlist_items';
  const STORAGE_EMAIL_KEY = 'aluma_wishlist_email';
  const SESSION_CAPTURE_KEY = 'aluma_wishlist_email_captured';
  const SESSION_POPUP_KEY = 'aluma_wishlist_popup_seen';
  const KLAVIYO_REVISION = '2026-01-15';
  const WISHLIST_HEART_SELECTOR = '.wishlist-btn[data-wishlist-toggle]';
  const NOTIFY_ME_SELECTOR = '.notify-me-btn[data-stock-notify-open]';
  const CART_DRAWER_SELECTOR = '[data-drawer="drawer-cart"]';

  const config = window.AlumaWishlistConfig || {};
  const state = {
    ids: [],
    items: {},
    customerLoggedIn: Boolean(config.customerLoggedIn),
    customerEmail: config.customerEmail || '',
    warnedMetafieldSync: false,
    activeModal: null,
    pendingWishlistProduct: null,
    pendingBackInStockProduct: null
  };

  function debugLog() {
    if (!window.console || typeof window.console.log !== 'function') return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift('[Aluma Wishlist]');
    window.console.log.apply(window.console, args);
  }

  function parseWishlistSeed(seed) {
    if (Array.isArray(seed)) return seed.map(String);

    if (typeof seed === 'string' && seed.trim() !== '') {
      try {
        const parsed = JSON.parse(seed);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (error) {
        return [];
      }
    }

    return [];
  }

  function uniqueIds(ids) {
    return ids.filter(function(id, index) {
      return ids.indexOf(id) === index;
    });
  }

  function readStorageJSON(key, fallback) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorageJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Keep the UI usable even if storage is blocked.
    }
  }

  function readStorageValue(storage, key) {
    try {
      return storage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStorageValue(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (error) {
      // Ignore storage failures.
    }
  }

  function getKnownEmail() {
    return readStorageValue(window.sessionStorage, SESSION_CAPTURE_KEY)
      || readStorageValue(window.localStorage, STORAGE_EMAIL_KEY)
      || state.customerEmail
      || '';
  }

  function persistLocalWishlist() {
    // The ID array is the source of truth for the wishlist.
    writeStorageJSON(STORAGE_KEY, state.ids);
    const persistedItems = {};

    state.ids.forEach(function(id) {
      if (state.items[id]) {
        persistedItems[id] = state.items[id];
      }
    });

    writeStorageJSON(STORAGE_ITEMS_KEY, persistedItems);
    debugLog('localStorage updated', STORAGE_KEY, readStorageJSON(STORAGE_KEY, []));
    debugLog('persisted wishlist item snapshots', persistedItems);
  }

  function normaliseProduct(raw) {
    if (!raw || !raw.productId) return null;

    return {
      productId: String(raw.productId),
      title: raw.title || '',
      url: raw.url || '',
      image: raw.image || '',
      price: raw.price || '',
      variantId: raw.variantId ? String(raw.variantId) : '',
      available: raw.available !== false && raw.available !== 'false'
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getProductFromWishlistToggle(button) {
    return normaliseProduct({
      productId: button.dataset.productId,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      variantId: button.dataset.productVariantId,
      available: button.dataset.productAvailable
    });
  }

  function getProductFromWishlistCard(card) {
    return normaliseProduct({
      productId: card.dataset.productId,
      title: card.dataset.productTitle,
      url: card.dataset.productUrl,
      image: card.dataset.productImage,
      price: card.dataset.productPrice,
      variantId: card.dataset.productVariantId,
      available: card.dataset.productAvailable
    });
  }

  function rememberProduct(product) {
    if (!product) return;
    state.items[product.productId] = Object.assign({}, state.items[product.productId] || {}, product);
  }

  function isWishlisted(productId) {
    return state.ids.indexOf(String(productId)) !== -1;
  }

  function warnMetafieldSyncLimit() {
    if (state.customerLoggedIn && !state.warnedMetafieldSync) {
      state.warnedMetafieldSync = true;
      console.warn(
        'Aluma wishlist: storefront theme code can seed from customer metafields, but it cannot securely write back to them without a server-side relay.'
      );
    }
  }

  function syncHeartButton(button) {
    const active = isWishlisted(button.dataset.productId);
    const title = button.dataset.productTitle || 'item';

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      active ? 'Remove ' + title + ' from wishlist' : 'Save ' + title + ' to wishlist'
    );
  }

  function syncWishlistUI() {
    document.querySelectorAll(WISHLIST_HEART_SELECTOR).forEach(syncHeartButton);

    document.querySelectorAll('[data-wishlist-badge-count]').forEach(function(el) {
      const count = String(state.ids.length);
      el.setAttribute('data-wishlist-badge-count', count);
      el.textContent = count;
    });
  }

  function animateHeart(button) {
    button.classList.remove('is-animating');
    window.requestAnimationFrame(function() {
      button.classList.add('is-animating');
      window.setTimeout(function() {
        button.classList.remove('is-animating');
      }, 220);
    });
  }

  function isVisibleElement(element) {
    return Boolean(element) && !element.hidden && window.getComputedStyle(element).display !== 'none';
  }

  function getNotifyButton(root) {
    return root ? root.querySelector(NOTIFY_ME_SELECTOR) : null;
  }

  function persistAndRefresh() {
    persistLocalWishlist();
    warnMetafieldSyncLimit();
    syncWishlistUI();
    renderWishlistPage();
  }

  function addWishlistItem(product) {
    if (!product) return;

    const productId = String(product.productId);
    debugLog('heart clicked, saving product ID', productId, product);
    if (!isWishlisted(productId)) {
      state.ids.unshift(productId);
      state.ids = uniqueIds(state.ids);
    }

    rememberProduct(product);
    persistAndRefresh();
  }

  function removeWishlistItem(productId) {
    debugLog('removing product ID', String(productId));
    state.ids = state.ids.filter(function(id) {
      return id !== String(productId);
    });
    persistAndRefresh();
  }

  function hydrateProductsFromDom() {
    document.querySelectorAll(WISHLIST_HEART_SELECTOR).forEach(function(button) {
      rememberProduct(getProductFromWishlistToggle(button));
    });

    document.querySelectorAll('[data-wishlist-item]').forEach(function(card) {
      rememberProduct(getProductFromWishlistCard(card));
    });
  }

  function createWishlistCardMarkup(product) {
    const title = escapeHtml(product.title || '');
    const url = escapeHtml(product.url || '#');
    const image = escapeHtml(product.image || '');
    const price = escapeHtml(product.price || '');
    const variantId = escapeHtml(product.variantId || '');

    const actionMarkup = product.available
      ? '<button type="button" class="btn btn--primary aluma-wishlist-card__add" data-wishlist-add-to-cart>Add to cart</button>'
      : '<button type="button" class="aluma-stock-notify-link notify-me-btn" data-stock-notify-open>NOTIFY ME WHEN AVAILABLE</button>';

    const preorderMarkup = product.preorder
      ? '<p class="body-size-3">' + escapeHtml(product.preorderLabel || 'Pre-order') + '</p>'
      : '';

    return (
      '<article class="aluma-wishlist-card" data-wishlist-item ' +
      'data-product-id="' + escapeHtml(product.productId) + '" ' +
      'data-product-title="' + escapeHtml(product.title || '') + '" ' +
      'data-product-url="' + url + '" ' +
      'data-product-image="' + image + '" ' +
      'data-product-price="' + escapeHtml(product.price || '') + '" ' +
      'data-product-variant-id="' + variantId + '" ' +
      'data-product-available="' + String(product.available) + '">' +
        '<a href="' + url + '" class="aluma-wishlist-card__image">' +
          (product.image ? '<img src="' + image + '" alt="' + escapeHtml(product.title || '') + '" loading="lazy">' : '') +
        '</a>' +
        '<div class="aluma-wishlist-card__body">' +
          '<div class="aluma-wishlist-card__top">' +
            '<a href="' + url + '" class="aluma-wishlist-card__title">' + title + '</a>' +
            '<button type="button" class="aluma-wishlist-card__remove" data-wishlist-remove aria-label="Remove ' + escapeHtml(product.title || 'item') + ' from wishlist">&times;</button>' +
          '</div>' +
          preorderMarkup +
          '<p class="aluma-wishlist-card__price">' + price + '</p>' +
          actionMarkup +
        '</div>' +
      '</article>'
    );
  }

  function renderWishlistPage() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    const grid = page.querySelector('[data-wishlist-grid]');
    const emptyState = page.querySelector('[data-wishlist-empty-state]');
    const addAllButton = page.querySelector('[data-wishlist-add-all]');
    const guestPrompt = page.querySelector('[data-wishlist-guest-prompt]');

    if (!grid || !emptyState || !addAllButton) return;

    const items = state.ids.map(function(id) {
      return state.items[id];
    }).filter(Boolean);

    debugLog('wishlist page localStorage IDs', readStorageJSON(STORAGE_KEY, []));
    debugLog('wishlist page cached items', state.items);
    if (state.ids.length > 0 && items.length === 0) {
      debugLog('wishlist page has saved IDs but no cached product snapshots yet', state.ids);
    }

    const hasWishlistItems = state.ids.length > 0;

    grid.innerHTML = items.map(createWishlistCardMarkup).join('');
    emptyState.hidden = hasWishlistItems;
    emptyState.style.display = hasWishlistItems ? 'none' : 'block';

    if (items.length > 0) {
      addAllButton.textContent = 'ADD ALL TO CART';
      addAllButton.dataset.wishlistActionMode = 'add-all';
      addAllButton.disabled = false;
      addAllButton.hidden = false;
    } else {
      addAllButton.textContent = 'DISCOVER MORE';
      addAllButton.dataset.wishlistActionMode = 'discover-more';
      addAllButton.disabled = false;
      addAllButton.hidden = false;
    }

    if (guestPrompt) {
      guestPrompt.hidden = state.customerLoggedIn || !hasWishlistItems;
    }
  }

  function getHandleFromProductUrl(url) {
    if (!url) return '';

    const cleanUrl = String(url).split('?')[0].replace(/\/+$/, '');
    const parts = cleanUrl.split('/');
    return parts[parts.length - 1] || '';
  }

  function formatPriceFromCents(cents) {
    const currency = (window.theme && (window.theme.currencyCode || window.theme.shopCurrency)) || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency
    }).format((Number(cents) || 0) / 100);
  }

  function fetchWishlistProductFromShopify(product) {
    const handle = getHandleFromProductUrl(product && product.url);
    if (!handle) {
      debugLog('missing product handle for saved item', product);
      return Promise.resolve();
    }

    // The Ajax /products/<handle>.js endpoint cannot expose metafields, so
    // pre-order status comes from a Liquid-rendered section instead.
    return window.fetch('/products/' + handle + '?section_id=api-wishlist-product')
      .then(function(response) {
        return response.ok ? response.text() : Promise.reject(response);
      })
      .then(function(html) {
        const payload = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-wishlist-product]');

        if (!payload) return Promise.reject(new Error('wishlist product payload missing'));

        const data = JSON.parse(payload.textContent);
        const freshProduct = {
          productId: String(data.productId),
          title: data.title || product.title,
          url: data.url || product.url,
          image: data.image || product.image,
          price: formatPriceFromCents(data.priceCents),
          variantId: data.variantId ? String(data.variantId) : product.variantId,
          available: Boolean(data.available),
          preorder: Boolean(data.preorder),
          preorderLabel: data.preorderLabel || '',
          saleTypeKey: data.saleTypeKey || '',
          saleTypeValue: data.saleTypeValue || ''
        };

        state.items[freshProduct.productId] = freshProduct;
        debugLog('fetched Shopify product for wishlist', freshProduct.productId, freshProduct);
      })
      .catch(function(error) {
        debugLog('wishlist section fetch failed, falling back to Ajax API', handle, error);
        return fetchWishlistProductViaAjax(product, handle);
      });
  }

  function fetchWishlistProductViaAjax(product, handle) {
    return window.fetch('/products/' + handle + '.js')
      .then(function(response) {
        return response.ok ? response.json() : Promise.reject(response);
      })
      .then(function(shopifyProduct) {
        const cached = state.items[String(shopifyProduct.id)] || {};
        const freshProduct = {
          productId: String(shopifyProduct.id),
          title: shopifyProduct.title || product.title,
          url: '/products/' + shopifyProduct.handle,
          image: shopifyProduct.featured_image || product.image,
          price: formatPriceFromCents(shopifyProduct.price),
          variantId: shopifyProduct.variants && shopifyProduct.variants[0] ? String(shopifyProduct.variants[0].id) : product.variantId,
          available: Boolean(shopifyProduct.available),
          // This endpoint has no metafields, so keep whatever pre-order state we already knew.
          preorder: Boolean(cached.preorder),
          preorderLabel: cached.preorderLabel || '',
          saleTypeKey: cached.saleTypeKey || '',
          saleTypeValue: cached.saleTypeValue || ''
        };

        state.items[freshProduct.productId] = freshProduct;
        debugLog('fetched Shopify product for wishlist', freshProduct.productId, freshProduct);
      })
      .catch(function(error) {
        debugLog('Shopify product fetch failed for wishlist item', handle, error);
      });
  }

  function refreshWishlistProductsFromShopify() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page || state.ids.length === 0) return;

    const productsToRefresh = state.ids.map(function(id) {
      return state.items[id];
    }).filter(Boolean);

    if (productsToRefresh.length === 0) {
      debugLog('wishlist page has IDs but no cached product snapshots', state.ids);
      return;
    }

    debugLog('refreshing wishlist products from Shopify', productsToRefresh.map(function(product) {
      return product.productId;
    }));

    Promise.all(productsToRefresh.map(fetchWishlistProductFromShopify)).finally(function() {
      persistLocalWishlist();
      renderWishlistPage();
    });
  }

  function parseKlaviyoError(response) {
    return response.text().then(function(text) {
      let detail = text;

      try {
        const parsed = JSON.parse(text);
        if (parsed && parsed.errors && parsed.errors.length) {
          detail = parsed.errors.map(function(error) {
            return error.detail || error.title || JSON.stringify(error);
          }).join(' | ');
        }
      } catch (error) {
        // Keep raw response text if parsing fails.
      }

      throw new Error('Klaviyo request failed (' + response.status + '): ' + detail);
    });
  }

  function setFormMessage(element, text, isError) {
    if (!element) return;
    element.hidden = false;
    element.textContent = text;
    element.style.color = isError ? '#b42318' : '#111';
  }

  function clearFormMessage(element) {
    if (!element) return;
    element.hidden = true;
    element.textContent = '';
    element.style.color = '';
  }

  function subscribeToKlaviyoBackInStock(email, variantId) {
    return window.fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_API_KEY), {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + variantId
              }
            }
          }
        }
      })
    })
      .then(function(response) {
        // Native BIS returns 202 with an empty body; do not JSON-parse it.
        if (response.status === 202 || response.ok) {
          return {};
        }
        return parseKlaviyoError(response);
      })
      .catch(function(error) {
        console.error('Aluma wishlist: Klaviyo back-in-stock subscribe failed.', error);
        throw error;
      });
  }

  function subscribeEmailToKlaviyoList(email) {
    return window.fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_API_KEY), {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: KLAVIYO_LIST_ID
              }
            }
          }
        }
      })
    })
      .then(function(response) {
        return response.ok ? response.json().catch(function() { return {}; }) : parseKlaviyoError(response);
      })
      .catch(function(error) {
        console.error('Aluma wishlist: Klaviyo subscribe failed.', error);
        throw error;
      });
  }

  function trackKlaviyoEvent(email, metricName, properties) {
    if (!email) return Promise.resolve();

    return window.fetch('https://a.klaviyo.com/client/events?company_id=' + encodeURIComponent(KLAVIYO_API_KEY), {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: properties,
            metric: {
              data: {
                type: 'metric',
                attributes: {
                  name: metricName
                }
              }
            },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          }
        }
      })
    })
      .then(function(response) {
        return response.ok ? response.json().catch(function() { return {}; }) : parseKlaviyoError(response);
      })
      .catch(function(error) {
        console.error('Aluma wishlist: Klaviyo event failed.', error);
        throw error;
      });
  }

  function buildModals() {
    if (document.querySelector('[data-wishlist-capture-modal]')) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = (
      '<div>' +
        '<div class="aluma-overlay-modal" data-wishlist-capture-modal aria-hidden="true">' +
          '<div class="aluma-overlay-modal__backdrop" data-modal-close></div>' +
          '<div class="aluma-overlay-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="aluma-wishlist-modal-title">' +
            '<button type="button" class="aluma-overlay-modal__close" data-modal-close aria-label="Close">&times;</button>' +
            '<div class="aluma-overlay-modal__inner">' +
              '<h2 class="aluma-overlay-modal__title" id="aluma-wishlist-modal-title">Save Your Wishlist</h2>' +
              '<p class="aluma-overlay-modal__copy">Enter your email to keep your favourites safe</p>' +
              '<form class="aluma-overlay-modal__form" data-wishlist-capture-form>' +
                '<input type="email" class="aluma-overlay-modal__input" name="email" placeholder="Email address" autocomplete="email" required>' +
                '<label class="aluma-overlay-modal__checkbox">' +
                  '<input type="checkbox" name="marketing_opt_in">' +
                  '<span>Join the Aluma Club for new arrivals and exclusive offers</span>' +
                '</label>' +
                '<button type="submit" class="btn btn--primary aluma-overlay-modal__submit">SAVE MY WISHLIST</button>' +
                '<p class="aluma-overlay-modal__message" hidden data-wishlist-capture-message></p>' +
              '</form>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="aluma-overlay-modal" data-back-in-stock-modal aria-hidden="true">' +
          '<div class="aluma-overlay-modal__backdrop" data-modal-close></div>' +
          '<div class="aluma-overlay-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="aluma-stock-modal-title">' +
            '<button type="button" class="aluma-overlay-modal__close" data-modal-close aria-label="Close">&times;</button>' +
            '<div class="aluma-overlay-modal__inner">' +
              '<h2 class="aluma-overlay-modal__title" id="aluma-stock-modal-title">Notify when back in stock</h2>' +
              '<p class="aluma-overlay-modal__copy">Receive an email when this item comes back in stock</p>' +
              '<form class="aluma-overlay-modal__form" data-back-in-stock-form>' +
                '<input type="email" class="aluma-overlay-modal__input" name="email" placeholder="Email address" autocomplete="email" required>' +
                '<label class="aluma-overlay-modal__checkbox">' +
                  '<input type="checkbox" name="marketing_opt_in">' +
                  '<span>Join the Aluma Club for new arrivals and exclusive offers</span>' +
                '</label>' +
                '<button type="submit" class="btn btn--primary aluma-overlay-modal__submit">JOIN THE LIST</button>' +
                '<p class="aluma-overlay-modal__message" hidden data-back-in-stock-message></p>' +
              '</form>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );

    document.body.appendChild(wrapper.firstElementChild);
  }

  function openModal(modal) {
    if (!modal) return;
    state.activeModal = modal;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    if (state.activeModal === modal) state.activeModal = null;
  }

  function openWishlistCaptureModal(product) {
    buildModals();

    const modal = document.querySelector('[data-wishlist-capture-modal]');
    const emailField = modal.querySelector('input[name="email"]');

    state.pendingWishlistProduct = product;
    writeStorageValue(window.sessionStorage, SESSION_POPUP_KEY, 'true');
    openModal(modal);

    if (emailField) {
      emailField.value = getKnownEmail();
      emailField.focus();
    }
  }

  function openSpotlightWaitlistModal(modal) {
    if (!modal) return;

    const emailField = modal.querySelector('input[name="email"]');
    clearFormMessage(modal.querySelector('[data-aluma-klaviyo-waitlist-message]'));
    openModal(modal);

    if (emailField) {
      emailField.value = getKnownEmail();
      window.setTimeout(function() {
        emailField.focus();
      }, 120);
    }
  }

  function openBackInStockModal(product) {
    buildModals();

    const modal = document.querySelector('[data-back-in-stock-modal]');
    if (!modal) return;

    const emailField = modal.querySelector('input[name="email"]');

    state.pendingBackInStockProduct = product;
    openModal(modal);

    if (emailField) {
      emailField.value = getKnownEmail();
      emailField.focus();
    }
  }

  function openCartDrawerAfterWishlistAdd() {
    const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
    if (!cartDrawer) return Promise.resolve();

    const cartRoute = (config.routes && config.routes.cart) || '/cart';

    return window.fetch(cartRoute + '.js')
      .then(function(response) {
        return response.ok ? response.json() : Promise.reject(response);
      })
      .then(function(cart) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          detail: { cart: cart },
          bubbles: true
        }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
        document.dispatchEvent(new CustomEvent('theme:cart:drawer:html-loaded', { bubbles: true }));
      })
      .catch(function() {});
  }

  function cartAddItemFromProduct(product) {
    const item = {
      id: Number(product.variantId),
      quantity: 1
    };

    if (product.preorder) {
      item.properties = {};
      item.properties[product.saleTypeKey || 'Sale type'] = product.saleTypeValue || product.preorderLabel || 'Pre-order';
    }

    return item;
  }

  function ensureProductHasSaleTypeMeta(product) {
    if (!product) return Promise.resolve(null);
    if (product.saleTypeKey) return Promise.resolve(product);

    return fetchWishlistProductFromShopify(product).then(function() {
      return state.items[product.productId] || product;
    });
  }

  function addProductsToCart(products, trigger) {
    const addable = (products || []).filter(function(product) {
      return product && product.variantId;
    });
    if (!addable.length) return Promise.resolve();

    if (trigger) trigger.disabled = true;

    const cartAddRoute = (config.routes && config.routes.cartAdd) || '/cart/add';
    return Promise.all(addable.map(ensureProductHasSaleTypeMeta))
      .then(function(readyProducts) {
        return window.fetch(cartAddRoute + '.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: readyProducts.filter(Boolean).map(cartAddItemFromProduct)
          })
        });
      })
      .then(function(response) {
        return response.ok ? response.json() : Promise.reject(response);
      })
      .then(function() {
        return openCartDrawerAfterWishlistAdd();
      })
      .catch(function(error) {
        console.error('Aluma wishlist: add to cart failed.', error);
      })
      .finally(function() {
        if (trigger) trigger.disabled = false;
      });
  }

  function getCurrentNotifyProduct(trigger) {
    const root = trigger.closest('[data-stock-notify-root], [data-wishlist-item]');
    if (!root) return null;

    const hiddenVariantInput = root.querySelector('input[name="id"]');
    return normaliseProduct({
      productId: root.dataset.productId,
      title: root.dataset.productTitle,
      url: root.dataset.productUrl,
      image: root.dataset.productImage,
      price: root.dataset.productPrice,
      variantId: trigger.dataset.variantId || (hiddenVariantInput ? hiddenVariantInput.value : ''),
      available: false
    });
  }

  function syncStockNotifyRoot(root, variant) {
    if (!root) return;

    const addButton = root.querySelector('[data-stock-add-button]');
    const notifyButton = getNotifyButton(root);
    const paymentButton = root.querySelector('[data-stock-dynamic-payment]');
    const hiddenVariantInput = root.querySelector('input[name="id"]');

    const currentVariantId = variant && variant.id ? String(variant.id) : (hiddenVariantInput ? hiddenVariantInput.value : '');
    const isAvailable = Boolean(variant ? variant.available : (notifyButton ? notifyButton.hidden : true));

    if (notifyButton) {
      notifyButton.dataset.variantId = currentVariantId;
      notifyButton.hidden = isAvailable;
    }

    if (addButton) {
      addButton.disabled = !isAvailable;
    }

    if (paymentButton) {
      paymentButton.hidden = !isAvailable;
    }
  }

  function syncStockNotifyButtonsOnPage() {
    document.querySelectorAll('[data-stock-notify-root]').forEach(function(root) {
      const hiddenVariantInput = root.querySelector('input[name="id"]');
      const notifyButton = getNotifyButton(root);
      const isAvailable = notifyButton ? notifyButton.hidden : true;
      syncStockNotifyRoot(root, {
        id: hiddenVariantInput ? hiddenVariantInput.value : '',
        available: isAvailable
      });
    });
  }

  function maybeOpenWishlistCapture(product) {
    if (state.customerLoggedIn) return;
    if (getKnownEmail()) return;
    if (readStorageValue(window.sessionStorage, SESSION_POPUP_KEY) === 'true') return;
    openWishlistCaptureModal(product);
  }

  function toggleWishlist(button) {
    const product = getProductFromWishlistToggle(button);
    if (!product) return;

    if (isWishlisted(product.productId)) {
      removeWishlistItem(product.productId);
      animateHeart(button);
      return;
    }

    // Save immediately so the wishlist page reflects the click right away.
    addWishlistItem(product);
    animateHeart(button);

    // Guests get the capture popup only once per session.
    maybeOpenWishlistCapture(product);

    const knownEmail = getKnownEmail();
    if (knownEmail) {
      trackKlaviyoEvent(knownEmail, 'Wishlist Item Added', {
        product_id: product.productId,
        product_title: product.title,
        product_url: product.url,
        product_image: product.image,
        product_price: product.price
      });
    }
  }

  function handleWishlistCaptureSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const emailField = form.querySelector('input[name="email"]');
    const marketingField = form.querySelector('input[name="marketing_opt_in"]');
    const email = emailField ? emailField.value.trim() : '';

    if (!email) return;

    writeStorageValue(window.sessionStorage, SESSION_CAPTURE_KEY, email);
    writeStorageValue(window.localStorage, STORAGE_EMAIL_KEY, email);

    const requests = [];
    if (marketingField && marketingField.checked) {
      requests.push(subscribeEmailToKlaviyoList(email));
    }

    if (state.pendingWishlistProduct) {
      requests.push(trackKlaviyoEvent(email, 'Wishlist Item Added', {
        product_id: state.pendingWishlistProduct.productId,
        product_title: state.pendingWishlistProduct.title,
        product_url: state.pendingWishlistProduct.url,
        product_image: state.pendingWishlistProduct.image,
        product_price: state.pendingWishlistProduct.price
      }));
    }

    Promise.all(requests).finally(function() {
      closeModal(document.querySelector('[data-wishlist-capture-modal]'));
      state.pendingWishlistProduct = null;
    });
  }

  function handleWaitlistFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const emailField = form.querySelector('input[name="email"]');
    const messageField = form.querySelector('[data-aluma-klaviyo-waitlist-message]');
    const submitButton = form.querySelector('button[type="submit"]');
    const email = emailField ? emailField.value.trim() : '';

    if (!email) return;

    clearFormMessage(messageField);
    writeStorageValue(window.localStorage, STORAGE_EMAIL_KEY, email);
    writeStorageValue(window.sessionStorage, SESSION_CAPTURE_KEY, email);

    const properties = {
      source: form.dataset.waitlistSource || 'product_spotlight'
    };

    if (form.dataset.waitlistHeading) {
      properties.spotlight_heading = form.dataset.waitlistHeading;
    }

    if (submitButton) {
      submitButton.disabled = true;
    }

    Promise.all([
      subscribeEmailToKlaviyoList(email),
      trackKlaviyoEvent(email, 'Waitlist Signup', properties)
    ])
      .then(function() {
        const spotlightModal = form.closest('[data-aluma-spotlight-waitlist-modal]');

        setFormMessage(messageField, 'You are on the list. We will be in touch soon.', false);

        if (spotlightModal) {
          window.setTimeout(function() {
            closeModal(spotlightModal);
            form.reset();
            clearFormMessage(messageField);
          }, 1400);
          return;
        }

        form.reset();
        if (emailField) {
          emailField.value = email;
        }
      })
      .catch(function(error) {
        console.error('Aluma wishlist: Waitlist signup failed.', error);
        setFormMessage(messageField, 'Something went wrong. Please try again.', true);
      })
      .finally(function() {
        if (submitButton) {
          submitButton.disabled = false;
        }
      });
  }

  function bindWaitlistForms() {
    document.querySelectorAll('[data-aluma-klaviyo-waitlist-form]').forEach(function(form) {
      if (form.dataset.bound) return;

      form.dataset.bound = 'true';
      form.addEventListener('submit', handleWaitlistFormSubmit);

      const emailField = form.querySelector('input[name="email"]');
      const knownEmail = getKnownEmail();

      if (emailField && knownEmail && !emailField.value) {
        emailField.value = knownEmail;
      }
    });
  }

  function handleBackInStockSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const emailField = form.querySelector('input[name="email"]');
    const marketingField = form.querySelector('input[name="marketing_opt_in"]');
    const messageField = form.querySelector('[data-back-in-stock-message]');
    const submitButton = form.querySelector('button[type="submit"]');
    const email = emailField ? emailField.value.trim() : '';
    const product = state.pendingBackInStockProduct;

    if (!email || !product) return;

    clearFormMessage(messageField);

    if (!product.variantId) {
      setFormMessage(messageField, 'Something went wrong. Please try again.', true);
      return;
    }

    writeStorageValue(window.localStorage, STORAGE_EMAIL_KEY, email);
    writeStorageValue(window.sessionStorage, SESSION_CAPTURE_KEY, email);

    const requests = [
      subscribeToKlaviyoBackInStock(email, product.variantId)
    ];

    if (marketingField && marketingField.checked) {
      requests.push(subscribeEmailToKlaviyoList(email));
    }

    if (submitButton) {
      submitButton.disabled = true;
    }

    Promise.all(requests)
      .then(function() {
        setFormMessage(messageField, 'Success. We will email you when this item is back in stock.', false);
        window.setTimeout(function() {
          closeModal(document.querySelector('[data-back-in-stock-modal]'));
          state.pendingBackInStockProduct = null;
          form.reset();
          clearFormMessage(messageField);
        }, 1400);
      })
      .catch(function(error) {
        console.error('Aluma wishlist: Back in stock submit failed.', error);
        setFormMessage(messageField, 'Something went wrong. Please try again.', true);
      })
      .finally(function() {
        if (submitButton) {
          submitButton.disabled = false;
        }
      });
  }

  function handleWishlistHeartClick(event) {
    const toggle = event.currentTarget;
    if (!toggle || !toggle.matches(WISHLIST_HEART_SELECTOR)) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    debugLog('wishlist heart handler fired', toggle.dataset.productId);
    toggleWishlist(toggle);
  }

  function handleNotifyMeClick(event) {
    const notifyTrigger = event.currentTarget;
    if (!notifyTrigger || !notifyTrigger.matches(NOTIFY_ME_SELECTOR)) return;
    if (!isVisibleElement(notifyTrigger)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    debugLog('notify me handler fired', notifyTrigger.dataset.variantId || '');
    openBackInStockModal(getCurrentNotifyProduct(notifyTrigger));
  }

  function bindActionTriggers() {
    document.querySelectorAll(WISHLIST_HEART_SELECTOR).forEach(function(toggle) {
      if (toggle.dataset.wishlistBound === 'true') return;
      toggle.dataset.wishlistBound = 'true';
      toggle.addEventListener('click', handleWishlistHeartClick);
    });

    document.querySelectorAll(NOTIFY_ME_SELECTOR).forEach(function(trigger) {
      if (trigger.dataset.stockNotifyBound === 'true') return;
      trigger.dataset.stockNotifyBound = 'true';
      trigger.addEventListener('click', handleNotifyMeClick);
    });
  }

  function handleDocumentClick(event) {
    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      const card = removeButton.closest('[data-wishlist-item]');
      if (card) removeWishlistItem(card.dataset.productId);
      return;
    }

    const addButton = event.target.closest('[data-wishlist-add-to-cart]');
    if (addButton) {
      const card = addButton.closest('[data-wishlist-item]');
      const product = card ? getProductFromWishlistCard(card) : null;
      const snapshot = product && state.items[product.productId]
        ? Object.assign({}, state.items[product.productId], product)
        : product;
      if (snapshot && snapshot.variantId) {
        addProductsToCart([snapshot], addButton);
      }
      return;
    }

    const addAllButton = event.target.closest('[data-wishlist-add-all]');
    if (addAllButton) {
      const actionMode = addAllButton.dataset.wishlistActionMode || 'discover-more';

      if (actionMode === 'discover-more') {
        const discoverUrl = addAllButton.dataset.discoverUrl || 'https://alumahome.co.uk/collections/all-products';
        window.location.href = discoverUrl;
        return;
      }

      const products = state.ids.map(function(id) {
        return state.items[id] && state.items[id].available ? state.items[id] : null;
      }).filter(Boolean);

      addProductsToCart(products, addAllButton);
      return;
    }

    const spotlightOpen = event.target.closest('[data-aluma-spotlight-waitlist-open]');
    if (spotlightOpen) {
      event.preventDefault();
      const modalId = spotlightOpen.getAttribute('aria-controls');
      const modal = modalId ? document.getElementById(modalId) : null;
      openSpotlightWaitlistModal(modal);
      return;
    }

    const modalClose = event.target.closest('[data-modal-close]');
    if (modalClose) {
      closeModal(modalClose.closest('.aluma-overlay-modal'));
    }
  }

  function bindModalEvents() {
    buildModals();

    const wishlistForm = document.querySelector('[data-wishlist-capture-form]');
    const stockForm = document.querySelector('[data-back-in-stock-form]');

    if (wishlistForm && !wishlistForm.dataset.bound) {
      wishlistForm.dataset.bound = 'true';
      wishlistForm.addEventListener('submit', handleWishlistCaptureSubmit);
    }

    if (stockForm && !stockForm.dataset.bound) {
      stockForm.dataset.bound = 'true';
      stockForm.addEventListener('submit', handleBackInStockSubmit);
    }

    document.querySelectorAll('.aluma-overlay-modal').forEach(function(modal) {
      if (modal.dataset.bound) return;
      modal.dataset.bound = 'true';
      modal.addEventListener('keydown', function(event) {
        if (event.key === 'Escape') closeModal(modal);
      });
    });
  }

  function observeDomChanges() {
    let queued = false;

    const observer = new MutationObserver(function() {
      if (queued) return;

      queued = true;
      window.requestAnimationFrame(function() {
        queued = false;
        hydrateProductsFromDom();
        bindActionTriggers();
        bindModalEvents();
        bindWaitlistForms();
        syncWishlistUI();
        syncStockNotifyButtonsOnPage();
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function initialiseWishlist() {
    const storedIds = readStorageJSON(STORAGE_KEY, []);
    const safeStoredIds = Array.isArray(storedIds) ? storedIds.map(String) : [];
    const storedItems = readStorageJSON(STORAGE_ITEMS_KEY, {});

    debugLog('wishlist.js initialising on', window.location.pathname);
    state.ids = uniqueIds(safeStoredIds.concat(parseWishlistSeed(config.customerWishlist)));
    state.items = storedItems && typeof storedItems === 'object' ? storedItems : {};

    debugLog('initial localStorage read', STORAGE_KEY, safeStoredIds);

    hydrateProductsFromDom();
    persistLocalWishlist();
    bindModalEvents();
    bindActionTriggers();
    bindWaitlistForms();
    syncWishlistUI();
    renderWishlistPage();
    debugLog('wishlist page root found', Boolean(document.querySelector('[data-wishlist-page]')));
    refreshWishlistProductsFromShopify();
    syncStockNotifyButtonsOnPage();
    observeDomChanges();

    document.addEventListener('click', handleDocumentClick);
    window.addEventListener('pageshow', function() {
      syncWishlistUI();
      renderWishlistPage();
      refreshWishlistProductsFromShopify();
    });
    document.addEventListener('theme:variant:change', function(event) {
      const target = event.target;
      const root = target && target.closest
        ? (target.matches('[data-stock-notify-root]') ? target : target.querySelector('[data-stock-notify-root]') || target.closest('[data-stock-notify-root]'))
        : null;
      if (root && event.detail && event.detail.variant) {
        syncStockNotifyRoot(root, event.detail.variant);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseWishlist);
  } else {
    initialiseWishlist();
  }
})();
